import React from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';

import { ERROR_TYPE, ErrorInfo } from '#shared/types/common';
import StandardLayoutCommon from '#web-components/components/Layouts/Standard';
import { getRegistryTitle, getRegistryTitleFull } from '#shared/utils/registrySettings';
import LogoLink from '#web-components/components/Navbar/components/LogoLink';

import { selectorAsyncActionCriticalErrors } from 'store/asyncAction/selectors';
import { NavLinks } from 'components/Navbar';
import Menu from 'components/Menu';
import LoaderIcon from 'components/LoaderImage';
import LogoIcon from 'components/LogoImage';

import ErrorLayout from '../Error';

interface Props {
  children?: React.ReactNode;
  title: string;
  titleVariant?: string;
  description?: string;
  isLoading?: boolean;
  error?: ErrorInfo;
  backLink?: {
    title: string,
    path: string,
    RouterNavLink: React.ElementType,
  };
}

export default function StandardLayout({
  title,
  titleVariant,
  description,
  error,
  backLink,
  isLoading = false,
  children,
}: Props) {
  const { t, i18n } = useTranslation('text');
  const criticalError = useSelector(selectorAsyncActionCriticalErrors);
  const hasError = criticalError || (error && (!error.type || error.type === ERROR_TYPE.CRITICAL));
  const registryTitle = getRegistryTitle(REGISTRY_SETTINGS);
  const registryTitleFull = getRegistryTitleFull(REGISTRY_SETTINGS);

  if (hasError) {
    return (
      <ErrorLayout error={criticalError || error} />
    );
  }

  return (
    <StandardLayoutCommon
      t={i18n.getFixedT(i18n.language, 'domains')}
      title={title}
      titleVariant={titleVariant}
      registryName={registryTitle}
      registryFullName={registryTitleFull}
      description={description}
      backLink={backLink}
      isLoading={isLoading}
      loaderIcon={<LoaderIcon />}
      sidebar={{
        logo: <LogoLink appTitle={t('appTitle')} homePath="/" logoIcon={<LogoIcon />} navLinkComponent={RouterLink} />,
        navLinks: <NavLinks />,
        menu: <Menu />,
      }}
      supportChannelUrl={REGISTRY_ENVIRONMENT_VARIABLES.supportChannelUrl}
    >
      {children}
    </StandardLayoutCommon>
  );
}
